import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import './UserLookup.css';

export function UserLookup() {
  const auth = useAuth();
  const [query, setQuery] = useState('');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [showRawSubs, setShowRawSubs] = useState(false);

  const API_URL = process.env.REACT_APP_API_SERVER_URL;

  const handleLookup = async () => {
    if (!query.trim()) {
      setError('Please enter an email or user ID before lookup.');
      return;
    }

    if (auth.user?.expires_at && Date.now() / 1000 > auth.user.expires_at) {
      auth.removeUser();
      setError('Session expired. Please log out & in again.');
      return;
    }

    setLoading(true);
    setUserData(null);
    setError(null);
    setMessage('');

    try {
      const token = auth.user?.access_token;
      if (!token) throw new Error('Missing authentication token');

      const res = await fetch(`${API_URL}/lookup?emailId=${encodeURIComponent(query)}`, {
        headers: {
          Authorization: `${token}`,
        },
      });

      // ----------
      const contentType = res.headers.get("content-type");
      if (!res.ok) {
        if (res.status === 404) throw new Error('User not found');
        if (res.status === 403) throw new Error('Unauthorized access');
        throw new Error(`Server returned ${res.status}`);
      } else if (!contentType || !contentType.includes("application/json")) {
        throw new Error('Invalid response format received');
      }

      const data = await res.json();
      setUserData(data);
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // const handleReset = () => {
  //   setMessage('No Backend Action Setup for Reset Password');
  // };

  // const handleDelete = async () => {
  //   setMessage('No Backend Action Setup for Delete User');
  // };

  const orderedFields = [
    'user_id',
    'email',
    'source',
    'firstName',
    'lastName',
    'stripe_id',
    'facebook_login',
    'account_created'
  ];

  return (
    <div className="lookup-container">
      <h2 className="lookup-title">🔎 User Lookup</h2>
      <div className="lookup-form">
        <input
          type="text"
          placeholder="Enter Email or User ID"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="lookup-input"
        />
        <button onClick={handleLookup} className="lookup-button">Lookup</button>
      </div>

      {loading && <div className="lookup-status">Loading...</div>}
      {error && <div className="lookup-error">❌ {error}</div>}
      {message && <div className="lookup-message">✅ {message}</div>}

      {userData && (
        <div className="lookup-result">
          <h3>User Info</h3>
          <table className="lookup-table">
            <tbody>
              {orderedFields.map((key) => {
                let value = null;
                switch (key) {
                  case 'user_id':
                    value = userData.user_id || userData.userId;
                    break;
                  case 'plus_provider':
                    value = userData.plus_provider || '—';
                    break;
                  case 'plus_status':
                    value = userData.plus_status || '—';
                    break;
                  case 'plus_period_ends':
                  case 'audio_period_ends':
                  case 'account_created':
                    const raw = userData[key] || userData[key.replace('plus_', '').replace('audio_', '')];
                    value = raw
                      ? new Date(Number(raw) * 1000).toUTCString()
                      : '—';
                    break;
                  case 'audio_provider':
                    value = userData.audio_provider || '—';
                    break;
                  case 'audio_status':
                    value = userData.audio_status || '—';
                    break;
                  default:
                    value = userData[key] || '—';
                }
                return (
                  <tr key={key}>
                    <td className="lookup-key">{key}</td>
                    <td className="lookup-value">
                      {typeof value === 'object' ? (
                        <pre>{JSON.stringify(value, null, 2)}</pre>
                      ) : value}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {userData?.subscriptions && (
            <>
              <button
                onClick={() => setShowRawSubs(prev => !prev)}
                className="lookup-button"
                style={{ marginTop: '1rem' }}
              >
                {showRawSubs ? 'Hide' : 'Show'} Raw Subscriptions
              </button>

              {showRawSubs && (
                <div style={{
                  marginTop: '1rem',
                  background: '#f6f6f6',
                  border: '1px solid #ccc',
                  padding: '1rem',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  maxHeight: '300px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.9rem'
                }}>
                  <pre style={{ margin: 0 }}>
                    {JSON.stringify(userData.subscriptions, null, 2)}
                  </pre>
                </div>
              )}
            </>
          )}

          {/* Uncomment the following lines if you want to add reset and delete actions */}
          {/* <div className="lookup-actions">
            <button className="lookup-reset" onClick={handleReset}>Reset Password</button>
            <button className="lookup-delete" onClick={handleDelete}>Delete User</button>
          </div> */}
        </div>
      )}
    </div>
  );
}
